<script lang="ts">
	/**
	 * The control for choosing what kind of tax ID a number is (§10, §4.6).
	 *
	 * It replaced a text box captioned "Type", which asked the customer to know that a German VAT
	 * number is `eu_vat`, a Québec sales tax number is `ca_qst` and a British one is `gb_vat`. What
	 * they typed instead was `VAT`, and the payment provider answered the save with *"Invalid type:
	 * must be one of ad_nrt, ae_trn, …"* — after the row had already been stored here, so the store
	 * kept an identifier it could never send and the reverse charge the number was entered for
	 * never applied. The same property `CountrySelect` has is the fix: **what the customer types
	 * never becomes the value**, only an option can.
	 *
	 * The country does the finding, not the customer. Somebody entering a tax ID has one
	 * jurisdiction in mind and has usually just typed it into the address above, so their own
	 * documents are the first group in the list — two or three entries, at the top — and the other
	 * hundred and fourteen follow under "All countries" rather than being hidden. Hidden would be
	 * wrong: a company registered somewhere it does not bill from is ordinary, and a picker that
	 * omitted the case would make it unenterable. `SelectField` puts a search box over a list this
	 * long on its own, which is the fallback for the case the country cannot narrow.
	 *
	 * A value this build does not know is offered as an option of its own, flagged. Those exist —
	 * the free-text box wrote them — and dropping one silently would leave a row whose type reads
	 * as unset while the store still holds and sends it.
	 */

	import SelectField from './SelectField.svelte';
	import { countryName } from '../Countries';
	import { taxIDType, taxIDTypeGroups, taxIDTypeName } from '../TaxIDTypes';
	import type { TaxIDType } from '../TaxIDTypes';

	/** The wire value, e.g. `eu_vat`. `''` is "nothing chosen". Bindable. */
	export let value: string = '';
	/** The customer's billing country, which decides what is offered first. `''` offers nothing first. */
	export let country: string = '';
	/** `for`/`id` wiring — pass the same value as the enclosing `FormField`'s `id`. */
	export let id: string = '';
	export let disabled: boolean = false;
	/** Only set by tests. Left unset, names follow the reader's own locale. */
	export let locale: string | undefined = undefined;

	let cssClass: string = '';
	export { cssClass as class };

	$: groups = taxIDTypeGroups(country, locale);
	$: suggestedLabel = country.trim() === '' ? '' : countryName(country, locale);
	/**
	 * Whether the value being held is one this build knows.
	 *
	 * Decided here rather than by the caller: the control is the one thing that knows what it can
	 * offer, and a value it cannot offer still has to be shown.
	 */
	$: isUnrecognised = value.trim() !== '' && taxIDType(value) === null;

	function optionName(type: TaxIDType): string {
		return taxIDTypeName(type, locale);
	}

	/**
	 * The rows, in the order they are offered.
	 *
	 * Group headings rather than nesting, because `SelectField` draws a heading above the first row
	 * carrying one and keeps every option a single flat list — which is what lets the arrow keys and
	 * the search box walk one structure instead of two.
	 */
	$: items = [
		// Kept selectable, not silently replaced: it is what the store is holding.
		...(isUnrecognised ? [{ value, name: `${value} — not a type this store can send` }] : []),
		...groups.suggested.map(type => ({
			value: type.value,
			name: optionName(type),
			group: suggestedLabel
		})),
		...groups.others.map(type => ({
			value: type.value,
			name: optionName(type),
			group: groups.suggested.length > 0 ? 'All countries' : 'Tax ID types'
		}))
	];
</script>

<!-- `$$restProps` first so the explicit props win it, and so a caller can label the control:
     these sit in a repeated row with no visible label of their own. -->
<SelectField
	{...$$restProps}
	{id}
	class={cssClass}
	{disabled}
	{items}
	bind:value
	placeholder="Choose a type…"
	searchPlaceholder="Search tax ID types…"
	on:change
/>
